import { useEffect, useState } from "react";

interface PairConfig {
  label: string;
  base: number;
  pip: number;
  spreadPips: number;
  decimals: number;
}

interface Quote {
  mid: number;
  dir: number;
}

const PAIRS: PairConfig[] = [
  { label: "EUR/USD", base: 1.08426, pip: 0.0001, spreadPips: 1.2, decimals: 5 },
  { label: "GBP/USD", base: 1.27188, pip: 0.0001, spreadPips: 1.6, decimals: 5 },
  { label: "USD/JPY", base: 151.4255, pip: 0.01, spreadPips: 1.5, decimals: 3 },
  { label: "AUD/USD", base: 0.65846, pip: 0.0001, spreadPips: 1.2, decimals: 5 },
];

/* Keep the drift small so the numbers stay believable. */
const DRIFT_LIMIT_PIPS = 24;

const ORDER_TYPES = [
  { key: "Market", caption: "Fills now, at the best available price." },
  { key: "Limit", caption: "Fills only at your price or better." },
  { key: "Stop", caption: "Triggers at your level, then goes to market." },
  { key: "S/L", caption: "Triggers like a stop, capped by a limit." },
];

export default function OrderTicket() {
  const [quotes, setQuotes] = useState<Quote[]>(() =>
    PAIRS.map((pair) => ({ mid: pair.base, dir: 0 })),
  );
  const [selected, setSelected] = useState("Market");

  useEffect(() => {
    const id = window.setInterval(() => {
      setQuotes((prev) =>
        prev.map((quote, index) => {
          const { base, pip } = PAIRS[index];
          const stepPips = (Math.floor(Math.random() * 7) - 3) / 10;
          const raw = quote.mid + stepPips * pip;
          const next = Math.min(
            base + DRIFT_LIMIT_PIPS * pip,
            Math.max(base - DRIFT_LIMIT_PIPS * pip, raw),
          );
          const dir = next > quote.mid ? 1 : next < quote.mid ? -1 : 0;
          return { mid: next, dir };
        }),
      );
    }, 1300);
    return () => window.clearInterval(id);
  }, []);

  const activeType = ORDER_TYPES.find((type) => type.key === selected) ?? ORDER_TYPES[0];

  return (
    <div className="border-2 border-primary-500 bg-foreground-900">
      <div className="flex items-center justify-between px-4 py-3 border-b-2 border-primary-500">
        <span className="flex items-center gap-2 font-label text-[11px] uppercase tracking-[0.2em] text-primary-500">
          <span className="w-2 h-2 bg-primary-500 animate-pulse"></span>
          Simulated order ticket
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 bg-primary-500"></span>
          <span className="w-2.5 h-2.5 bg-accent-500"></span>
          <span className="w-2.5 h-2.5 bg-background-400"></span>
        </span>
      </div>

      <div className="grid grid-cols-3 px-4 py-2.5 border-b-2 border-primary-500/40 font-label text-[10px] uppercase tracking-[0.16em] text-background-400">
        <span>Pair</span>
        <span className="text-right">Bid</span>
        <span className="text-right">Ask</span>
      </div>

      <div className="divide-y divide-primary-500/20">
        {PAIRS.map((pair, index) => {
          const quote = quotes[index];
          const bid = quote.mid - (pair.spreadPips * pair.pip) / 2;
          const ask = quote.mid + (pair.spreadPips * pair.pip) / 2;
          return (
            <div
              key={pair.label}
              className="grid grid-cols-3 px-4 py-3 font-label text-xs text-background-200 tabular-nums"
            >
              <span className="flex items-center gap-1.5">
                <span className="text-background-50 font-semibold">{pair.label}</span>
                <i
                  className={`text-[11px] transition-colors ${
                    quote.dir > 0
                      ? "ri-arrow-up-fill text-secondary-400"
                      : quote.dir < 0
                        ? "ri-arrow-down-fill text-accent-400"
                        : "ri-subtract-line text-background-600"
                  }`}
                ></i>
              </span>
              <span className="text-right text-secondary-300">{bid.toFixed(pair.decimals)}</span>
              <span className="text-right text-accent-300">{ask.toFixed(pair.decimals)}</span>
            </div>
          );
        })}
      </div>

      <div className="p-4 border-t-2 border-primary-500">
        <p className="font-label text-[10px] uppercase tracking-[0.16em] text-background-400 mb-2">
          Order type
        </p>
        <div className="grid grid-cols-4 gap-1.5">
          {ORDER_TYPES.map((type) => {
            const active = type.key === selected;
            return (
              <button
                key={type.key}
                type="button"
                onClick={() => setSelected(type.key)}
                aria-pressed={active}
                className={`text-center py-2 font-label text-[10px] uppercase tracking-[0.1em] border-2 whitespace-nowrap transition-colors cursor-pointer ${
                  active
                    ? "bg-primary-500 border-primary-500 text-foreground-950 font-bold"
                    : "border-primary-500/40 text-background-300 hover:border-primary-500 hover:text-primary-500"
                }`}
              >
                {type.key}
              </button>
            );
          })}
        </div>
        <p className="mt-3 text-xs leading-relaxed text-background-300">{activeType.caption}</p>
      </div>

      <div className="px-4 py-2.5 border-t-2 border-primary-500/40">
        <p className="font-label text-[10px] uppercase tracking-[0.14em] text-background-500">
          Illustrative feed · prices update on their own · not real market data
        </p>
      </div>
    </div>
  );
}